import api from "./axios";
import { convertKeysToSnakeCase } from "../utils/caseConverter";
export const createStore = async ({
  name,
  logo,
  currency,
  timeZone,
  backgroundColor,
  primaryColor,
  secondaryColor,
}) => {
  const formData = new FormData();
  formData.append("name", name);
  formData.append("logo", logo);
  formData.append("currency", currency);
  if (timeZone) {
    formData.append("time_zone", timeZone);
  }
  formData.append("background_color", backgroundColor);
  formData.append("primary_color", primaryColor);
  formData.append("secondary_color", secondaryColor);

  const response = await api.post("/stores", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response.data;
};

export const deleteStore = async ({ storeId }) => {
  const response = await api.delete(`/stores/${storeId}`);
  return response.data;
};

export const inviteUser = async ({ storeId, userData }) => {
  const formData = new FormData();
  formData.append("email", userData.email);
  formData.append("role", userData.role);
  formData.append("dashboard_access", String(userData.dashboard_access));

  const response = await api.post(`/stores/${storeId}/store_users`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response.data;
};

export const updateStoreUserRole = async ({ storeId, storeUserId, role }) => {
  const response = await api.patch(
    `/stores/${storeId}/store_users/${storeUserId}`,
    {
      role,
    },
  );
  return response.data;
};

export const updateStoreUserDashboardAccess = async ({
  storeId,
  storeUserId,
  dashboardAccess,
}) => {
  const response = await api.patch(
    `/stores/${storeId}/store_users/${storeUserId}`,
    {
      dashboard_access: dashboardAccess,
    },
  );
  return response.data;
};

export const removeStoreUser = async ({ storeId, storeUserId }) => {
  const response = await api.delete(
    `/stores/${storeId}/store_users/${storeUserId}`,
  );
  return response.data;
};

export const createPassLayout = async ({
  storeId,
  layoutData,
  stampIcon,
  companyLogo,
  backgroundImagesFormData,
  milestoneIconsFormData,
  previewImage,
}) => {
  const formData = new FormData();

  // Convert layoutData from camelCase to snake_case
  const snakeCaseLayoutData = convertKeysToSnakeCase(layoutData);

  // Append the layout data as a JSON string
  formData.append("pass", JSON.stringify(snakeCaseLayoutData));

  // Handle company logo
  if (companyLogo) {
    formData.append("company_logo", companyLogo);
  }

  // Handle stamp icon
  if (stampIcon) {
    formData.append("stamp_icon", stampIcon);
  }
  // Handle background image
  if (backgroundImagesFormData) {
    backgroundImagesFormData.forEach((value, key) => {
      formData.append(key, value);
    });
  }

  // Freshly uploaded per-milestone icons (milestoneIcon_<index>)
  if (milestoneIconsFormData) {
    milestoneIconsFormData.forEach((value, key) => {
      formData.append(key, value);
    });
  }

  if (previewImage) {
    formData.append("preview_image", previewImage);
  }

  const response = await api.post(`/stores/${storeId}/passes`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response.data;
};

export const updatePassLayout = async ({
  storeId,
  passId,
  layoutData,
  stampIcon,
  companyLogo,
  backgroundImagesFormData,
  milestoneIconsFormData,
  previewImage,
}) => {
  const formData = new FormData();

  // Convert layoutData from camelCase to snake_case
  const snakeCaseLayoutData = convertKeysToSnakeCase(layoutData);

  // Append the layout data as a JSON string
  formData.append("pass", JSON.stringify(snakeCaseLayoutData));

  if (companyLogo) {
    formData.append("company_logo", companyLogo);
  }

  if (stampIcon) {
    formData.append("stamp_icon", stampIcon);
  }

  if (backgroundImagesFormData) {
    backgroundImagesFormData.forEach((value, key) => {
      formData.append(key, value);
    });
  }

  // Freshly uploaded per-milestone icons (milestoneIcon_<index>)
  if (milestoneIconsFormData) {
    milestoneIconsFormData.forEach((value, key) => {
      formData.append(key, value);
    });
  }

  if (previewImage) {
    formData.append("preview_image", previewImage);
  }

  const response = await api.put(
    `/stores/${storeId}/passes/${passId}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return response.data;
};

export const createForm = async ({
  storeId,
  kind,
  passId,
  description,
  submitText,
  content,
  backgroundColor,
  textColor,
  lang,
}) => {
  const response = await api.post(`/stores/${storeId}/forms`, {
    kind,
    passId,
    description,
    submit_text: submitText,
    pass_id: passId,
    content,
    background_color: backgroundColor,
    text_color: textColor,
    lang,
  });

  return response.data;
};

export const updateForm = async ({
  storeId,
  formId,
  kind,
  passId,
  description,
  submitText,
  content,
  backgroundColor,
  textColor,
}) => {
  const response = await api.put(`/stores/${storeId}/forms/${formId}`, {
    kind,
    description,
    submit_text: submitText,
    pass_id: passId,
    content,
    background_color: backgroundColor,
    text_color: textColor,
  });

  return response.data;
};

export const submitPublicForm = async ({ formId, data }) => {
  const response = await api.post(`/public/forms/${formId}/submit`, {
    submission: data,
  });
  return response.data;
};

export const deletePassLayout = async ({ storeId, passId }) => {
  const response = await api.delete(`/stores/${storeId}/passes/${passId}`);
  return response.data;
};

export const createPage = async ({ storeId, pageData }) => {
  const response = await api.post(`/stores/${storeId}/pages`, pageData);
  return response.data;
};

export const updatePage = async ({ storeId, pageId, pageData }) => {
  const response = await api.put(
    `/stores/${storeId}/pages/${pageId}`,
    pageData,
  );
  return response.data;
};

export const updatePageSettings = async ({ storeId, pageId, settings }) => {
  const response = await api.put(`/stores/${storeId}/pages/${pageId}`, {
    settings: settings,
  });
  return response.data;
};

export const createFlyer = async ({ storeId, formData }) => {
  const response = await api.post(`/stores/${storeId}/flyers`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

export const updateFlyer = async ({ storeId, flyerId, formData }) => {
  const response = await api.put(
    `/stores/${storeId}/flyers/${flyerId}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};

export const updateCustomer = async ({ storeId, customerId, data }) => {
  const response = await api.put(
    `/stores/${storeId}/customers/${customerId}`,
    data,
  );
  return response.data;
};

export const deleteCustomer = async ({ storeId, customerId }) => {
  const response = await api.delete(
    `/stores/${storeId}/customers/${customerId}`,
  );
  return response.data;
};

export const sendCustomerPass = async ({ storeId, customerId, email }) => {
  const response = await api.post(
    `/stores/${storeId}/customers/${customerId}/send_pass`,
    email ? { email } : {},
  );
  return response.data;
};

export const createCampaign = async ({ storeId, campaignData }) => {
  const response = await api.post(`/stores/${storeId}/campaigns`, campaignData);
  return response.data;
};

export const updateCampaign = async ({ storeId, campaignId, campaignData }) => {
  const response = await api.put(
    `/stores/${storeId}/campaigns/${campaignId}`,
    campaignData,
  );
  return response.data;
};

export const deleteCampaign = async ({ storeId, campaignId }) => {
  const response = await api.delete(
    `/stores/${storeId}/campaigns/${campaignId}`,
  );
  return response.data;
};

export const createSubscription = async ({ storeId, productId, interval }) => {
  const response = await api.post(`/stores/${storeId}/checkout`, {
    product_id: productId,
    interval: interval,
  });

  return response.data;
};

export const deleteForm = async ({ storeId, formId }) => {
  const response = await api.delete(`/stores/${storeId}/forms/${formId}`);
  return response.data;
};

export const deleteFlyer = async ({ storeId, flyerId }) => {
  const response = await api.delete(`/stores/${storeId}/flyers/${flyerId}`);
  return response.data;
};

export const updateStore = async ({
  storeId,
  name,
  logo,
  currency,
  timeZone,
  backgroundColor,
  primaryColor,
  secondaryColor,
  clubUrl,
  customDomain,
  hideFooter,
}) => {
  const formData = new FormData();
  formData.append("name", name);
  if (logo) {
    formData.append("logo", logo);
  }

  formData.append("currency", currency);

  // Skipped when empty so a save with the zone unset never touches it: an
  // omitted time_zone leaves the stored value as it is. A blank would be
  // accepted (the API normalizes it to null), but would clear the zone.
  if (timeZone) {
    formData.append("time_zone", timeZone);
  }

  if (backgroundColor) {
    formData.append("background_color", backgroundColor);
  }

  if (primaryColor) {
    formData.append("primary_color", primaryColor);
  }
  if (secondaryColor) {
    formData.append("secondary_color", secondaryColor);
  }

  // Add club-related fields
  if (clubUrl !== undefined) {
    formData.append("club_url", clubUrl);
  }
  if (customDomain !== undefined) {
    formData.append("custom_domain", customDomain);
  }
  if (hideFooter !== undefined) {
    formData.append("hide_footer", hideFooter);
  }

  const response = await api.put(`/stores/${storeId}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response.data;
};

export const updateCustomField = async ({ storeId, fieldId, data }) => {
  const response = await api.put(
    `/stores/${storeId}/custom_fields/${fieldId}`,
    data,
  );
  return response.data;
};

export const deleteCustomField = async ({ storeId, fieldId }) => {
  const response = await api.delete(
    `/stores/${storeId}/custom_fields/${fieldId}`,
  );
  return response.data;
};

export const createBranch = async ({
  storeId,
  name,
  address,
  latitude,
  longitude,
}) => {
  const response = await api.post(`/stores/${storeId}/branches`, {
    name,
    address,
    latitude,
    longitude,
  });
  return response.data;
};

export const updateBranch = async ({
  storeId,
  branchId,
  name,
  address,
  latitude,
  longitude,
}) => {
  const response = await api.patch(`/stores/${storeId}/branches/${branchId}`, {
    name,
    address,
    latitude,
    longitude,
  });
  return response.data;
};

export const deleteBranch = async ({ storeId, branchId }) => {
  const response = await api.delete(`/stores/${storeId}/branches/${branchId}`);
  return response.data;
};

export const createNotification = async ({ storeId, notificationData }) => {
  const response = await api.post(
    `/stores/${storeId}/notifications`,
    notificationData,
  );
  return response.data;
};

export const updateNotification = async ({
  storeId,
  notificationId,
  notificationData,
}) => {
  const response = await api.put(
    `/stores/${storeId}/notifications/${notificationId}`,
    notificationData,
  );
  return response.data;
};

export const deleteNotification = async ({ storeId, notificationId }) => {
  const response = await api.delete(
    `/stores/${storeId}/notifications/${notificationId}`,
  );
  return response.data;
};

export const updateUser = async ({ data }) => {
  const response = await api.patch(`/user`, data);
  return response.data;
};

export const connectIntegration = async ({ storeId, integrationData }) => {
  // silent: the integration drawer shows the API's message inline; the
  // global toast would repeat it (and print an `errors` hash as text).
  const response = await api.post(
    `/stores/${storeId}/integrations`,
    integrationData,
    { silent: true },
  );
  return response.data;
};

export const disconnectIntegration = async ({ storeId, integrationId }) => {
  const response = await api.delete(
    `/stores/${storeId}/integrations/${integrationId}`,
    { silent: true },
  );
  return response.data;
};

export const updateIntegration = async ({
  storeId,
  integrationId,
  integrationData,
}) => {
  const response = await api.put(
    `/stores/${storeId}/integrations/${integrationId}`,
    integrationData,
    { silent: true },
  );
  return response.data;
};

// Starts a background CSV export; poll fetchDashboardExport with the id.
export const createDashboardExport = async ({ storeId, type, segment }) => {
  const payload = { type };
  if (segment) payload.segment = segment;
  const response = await api.post(
    `/stores/${storeId}/dashboard/exports`,
    payload,
  );
  return response.data;
};

// JSON PATCH of the one field, so the switch never resends (or clears) the
// rest of the store the way the multipart updateStore form does.
export const updateStoreWeeklyReport = async ({
  storeId,
  weekly_report_enabled,
}) => {
  const response = await api.patch(`/stores/${storeId}`, {
    weekly_report_enabled,
  });
  return response.data;
};

export const createMessage = async ({ storeId, messageData }) => {
  // Format the data to ensure integration_id is properly passed
  const formattedData = {
    ...messageData,
    integration_id: messageData.integration_id
      ? parseInt(messageData.integration_id, 10)
      : null,
  };

  const response = await api.post(`/stores/${storeId}/messages`, formattedData);
  return response.data;
};

export const updateMessage = async ({ storeId, messageId, messageData }) => {
  // Format the data to ensure integration_id is properly passed
  const formattedData = {
    ...messageData,
    integration_id: messageData.integration_id
      ? parseInt(messageData.integration_id, 10)
      : null,
  };

  const response = await api.put(
    `/stores/${storeId}/messages/${messageId}`,
    formattedData,
  );
  return response.data;
};

export const deleteMessage = async ({ storeId, messageId }) => {
  const response = await api.delete(`/stores/${storeId}/messages/${messageId}`);
  return response.data;
};

// Card expiration, applied to the customers who already hold the card.
//
// The rule itself is saved by the ordinary pass update; these three only ever
// deal with the holders. previewPassExpiration is read-only and answers both
// "would this change anything?" and "what would it do to them?", which is what
// lets the editor offer the choice without guessing — the server owns the
// comparison, so the two can never disagree about what counts as a change.
export const previewPassExpiration = async ({ storeId, passId, layoutData }) => {
  const response = await api.post(
    `/stores/${storeId}/passes/${passId}/expiration_preview`,
    { pass: layoutData ? convertKeysToSnakeCase(layoutData) : undefined },
  );
  return response.data;
};

// Applies the card's SAVED rule, so the pass update has to have landed first.
export const applyPassExpiration = async ({ storeId, passId }) => {
  const response = await api.post(
    `/stores/${storeId}/passes/${passId}/apply_expiration`,
  );
  return response.data;
};

export const revertPassExpiration = async ({ storeId, passId }) => {
  const response = await api.post(
    `/stores/${storeId}/passes/${passId}/revert_expiration`,
  );
  return response.data;
};
